import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { fetchLineMetrics, LineMetrics } from './analysisApi';
import { useI18n } from '../i18n/I18nContext';

// Contrast scale shared with AccuracyBox: the previous rgba(255,255,255,0.35)
// label/muted tiers were ~2.5:1 on #0f2033 and unreadable.
const C = {
  bg:      '#0f2033',
  border:  'rgba(240,165,0,0.15)',
  value:   '#eef3f8',
  body:    '#c3ced9',
  label:   '#93a3b5',
  faint:   '#7d8fa3',
  amber:   '#f0a500',
  red:     '#f87171',
  green:   '#4ade80',
};

function pctColour(pct: number): string {
  if (pct >= 90) return '#4ade80';
  if (pct >= 70) return '#f0a500';
  return '#f87171';
}

type Props = {
  line: string | null;
  day:  string;
};

const LineMetricsBox: React.FC<Props> = ({ line, day }) => {
  const { t } = useI18n();
  const [metrics, setMetrics] = useState<LineMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!line) return;
    setLoading(true);
    setError(null);
    fetchLineMetrics(day, line)
      .then(setMetrics)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [line, day]);

  if (!line) return null;

  return (
    <Box sx={{
      px: '12px', py: '10px',
      borderBottom: `1px solid ${C.border}`,
    }}>
      <Typography sx={{
        fontFamily: '"IBM Plex Mono"', fontSize: '10.5px',
        letterSpacing: '0.1em', color: C.label,
        textTransform: 'uppercase', mb: '8px',
      }}>
        {t('metrics.title', { line })}
      </Typography>

      {loading && <CircularProgress size={14} sx={{ color: C.amber }} />}
      {error   && <Typography sx={{ fontSize: '10.5px', color: C.red, fontFamily: '"IBM Plex Mono"' }}>{error}</Typography>}

      {!loading && !error && metrics && (
        <>
          {/* Progress bar */}
          <Box sx={{
            height: '4px', borderRadius: '2px',
            backgroundColor: 'rgba(255,255,255,0.08)',
            mb: '10px', overflow: 'hidden',
          }}>
            <Box sx={{
              height: '100%',
              width: `${metrics.pct_success}%`,
              backgroundColor: pctColour(metrics.pct_success),
              borderRadius: '2px',
              transition: 'width 0.4s ease',
            }} />
          </Box>

          {/* Four metric tiles */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            {[
              { label: t('metrics.total'),     value: metrics.total,     color: C.value },
              { label: t('metrics.published'), value: metrics.published, color: C.green },
              { label: t('metrics.failed'),    value: metrics.failed,    color: C.red   },
              { label: t('metrics.noStatus'),  value: metrics.no_status, color: C.body  },
            ].map(({ label, value, color }) => (
              <Box key={label} sx={{
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: '4px',
                px: '8px', py: '6px',
              }}>
                <Typography sx={{
                  fontFamily: '"IBM Plex Mono"', fontSize: '9.5px',
                  letterSpacing: '0.08em', color: C.label,
                  textTransform: 'uppercase', mb: '2px',
                }}>
                  {label}
                </Typography>
                <Typography sx={{
                  fontFamily: '"IBM Plex Mono"', fontSize: '14px',
                  fontWeight: 600, color,
                }}>
                  {value.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Percentage callout */}
          <Box sx={{ mt: '8px', textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: '"IBM Plex Mono"', fontSize: '11.5px',
              color: pctColour(metrics.pct_success),
              letterSpacing: '0.06em',
            }}>
              {t('metrics.successRate', { pct: metrics.pct_success })}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LineMetricsBox;